import React, { useEffect, useState } from "react";
import RowLoading from "./RowLoading.tsx";
import EmptyImage from "../../../EmptyImage.tsx";
import { ReporteChequeo, ReporteRow } from "../../../../models/ReporteChequeo.ts";
import RowReporteChequeoCodigo from "./RowReporteChequeoCodigo.tsx";
import LastRowSummary from "./LastRowSummary.tsx";
import RowHourSum from "./RowHourSum.tsx";
import { fetchAssistanceByID } from "../../../../services/assistenceCode.ts";
import {
  formatDataForReport,
  BranchReportMeta,
} from "../../../../utils/reporteChequeoUtils.ts";
import { readHoursByBranch } from "../../../../services/branchSchedule.service.ts";

const parseTimeToMinutes = (time: string): number => {
  const [hours = "3", minutes = "0", seconds = "0"] = String(time || "03:00:00")
    .split(":")
    .map((v) => v.trim());
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  const s = Number(seconds) || 0;
  return h * 60 + m + s / 60;
};

const TableReporteChequeoCodigo = ({
  records,
  loading,
  start,
  end,
}: {
  records: ReporteChequeo[];
  loading: boolean;
  start: string;
  end: string;
}) => {
  const [reportData, setReportData] = useState<ReporteRow[]>([]);
  const [branchOperationalMeta, setBranchOperationalMeta] = useState<
    Record<number, BranchReportMeta>
  >({});

  useEffect(() => {
    const loadMetaByBranch = async () => {
      const branchIds = [
        ...new Set(
          (records || [])
            .map((record) => Number(record.branch_id))
            .filter((branchId) => Number.isFinite(branchId) && branchId > 0)
        ),
      ];

      if (branchIds.length === 0) {
        setBranchOperationalMeta({});
        return;
      }

      try {
        const entries = await Promise.all(
          branchIds.map(async (branchId) => {
            const hours = await readHoursByBranch(branchId);
            const nightScheduleEnabled = Boolean(hours?.nightScheduleEnabled);
            const cutoffMinutes = nightScheduleEnabled
              ? parseTimeToMinutes(
                  String(hours?.operationalDayCutoffTime || "03:00:00")
                )
              : 3 * 60;
            return [
              branchId,
              { nightScheduleEnabled, cutoffMinutes },
            ] as const;
          })
        );

        const next: Record<number, BranchReportMeta> = {};
        entries.forEach(([branchId, meta]) => {
          next[branchId] = meta;
        });
        setBranchOperationalMeta(next);
      } catch {
        setBranchOperationalMeta({});
      }
    };

    loadMetaByBranch();
  }, [records]);

  useEffect(() => {
    const input = records || [];
    if (input.length === 0) {
      setReportData([]);
      return;
    }

    const branchIds = [
      ...new Set(
        input
          .map((r) => Number(r.branch_id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    ];

    const effectiveMeta: Record<number, BranchReportMeta> = {};
    for (const id of branchIds) {
      effectiveMeta[id] = branchOperationalMeta[id] ?? {
        cutoffMinutes: 3 * 60,
        nightScheduleEnabled: false,
      };
    }

    setReportData(formatDataForReport(input, start, end, effectiveMeta));
  }, [records, start, end, branchOperationalMeta]);

  return (
    <div className="table-sticky mb-3">
      <table className="table table-hover table-panel text-nowrap align-middle mb-0">
        <thead className="sticky-thead table-light text-center">
          <tr>
            <th className="align-middle">Chilito</th>
            <th className="align-middle">Puesto</th>
            <th className="align-middle">Turno</th>
            <th className="align-middle">Fecha</th>
            <th className="align-middle">Entrada</th>
            <th className="align-middle">Salida</th>
            <th className="align-middle">Horas trabajadas</th>
            <th className="align-middle">Sucursal</th>
            <th className="align-middle">Acciones</th>
          </tr>
        </thead>
        <tbody className="accordion" id="accordionExample">
          {reportData.map((record, index) => (
            <React.Fragment
              key={`${record.branch_id}-${record.checkIn?.assistanceCheck_id}-${index}`}
            >
              <RowReporteChequeoCodigo record={record} />
              <RowHourSum
                index={index}
                records={reportData}
                payrollBranchMeta={branchOperationalMeta}
                allChecks={records}
              />
            </React.Fragment>
          ))}

          <LastRowSummary records={reportData} />

          {loading &&
            Array.from({ length: 15 }).map((_, index) => (
              <RowLoading key={index} />
            ))}
          {!loading && records.length === 0 && (
            <tr className="text-center">
              <td colSpan={9}>
                <div className="py-4 d-flex flex-column align-items-center justify-content-center">
                  <EmptyImage className="mx-auto w-250px" />
                  <h5 className="text-secondary text-center fs-20px mt-3">
                    No se encontraron registros
                  </h5>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableReporteChequeoCodigo;
